import { FC, useEffect, useState } from "react";
import { useProfesional } from "../context/ProfesionalContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  ButtonPopup,
  ButtonSiguiente,
  ButtonVolver,
  Input,
  Select,
} from "@/components/ui";
import { safeFetch } from "@/api/safeFetch";
import { AlertaType } from "@/types/AlertaType";
import Alerta from "@/components/alert/Alerta";
import { mapUIToLandingSettings } from "../mapper/landingMapper";
import { normalizeLandingData } from "../core/buildLandingData";
import { DEFAULT_THEME } from "../types/DefaultTheme";
import { uploadBackgroundLanding } from "../subirImagen/subirImagen";

const EditarFondoWeb: FC = () => {
  const navigate = useNavigate();
  const { landingData, setLandingData, perfilId } = useProfesional();
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const { profesionalId, profesionId } = useParams();

  type AlertState = {
    mensaje: string;
    type: AlertaType;
  };

  const [alerta, setAlerta] = useState<AlertState | null>(null);
  const [loading, setLoading] = useState(false);

  const theme = landingData?.theme ?? {
    backgroundType: "image",
    backgroundImage: "",
    backgroundColor: "#000",
    globalStyles: {
      fontFamily: "Inter",
      textColor: "#fff",
    },
    components: {},
  };
  const globalStyles = theme?.globalStyles;

  useEffect(() => {
    if (!perfilId) return;

    let active = true;

    const load = async () => {
      const res = await safeFetch(`/api/landing/${perfilId}/theme`, {
        cache: "no-store",
      });

      if (!res.ok) return;

      const data = await res.json();

      if (active) {
        setLandingData(normalizeLandingData(data));
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [perfilId]);

  const updateGlobalStyles = (patch: any) => {
    setLandingData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        theme: {
          ...prev.theme,
          globalStyles: {
            ...prev.theme?.globalStyles,
            ...patch,
          },
        },
      };
    });
  };

  const handleSave = async () => {
    if (loading || !perfilId || !landingData) return;

    setLoading(true);

    try {
      const payload = mapUIToLandingSettings({
        ...landingData,
        theme: {
          ...landingData.theme,
          backgroundImage: landingData.theme.backgroundImage?.startsWith(
            "blob:",
          )
            ? ""
            : landingData.theme.backgroundImage,
        },
      });

      const res = await safeFetch(`/api/landing/${perfilId}/theme`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res?.ok) {
        throw new Error("Backend error");
      }

      // 🔥 Recargar desde backend para evitar datos viejos

      setAlerta({
        mensaje: "Datos guardados correctamente",
        type: "success",
      });
    } catch (err) {
      console.error(err);

      setAlerta({
        mensaje: "Error al guardar los datos",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetTheme = () => {
    setLandingData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        theme: {
          ...DEFAULT_THEME,
          globalStyles: {
            ...DEFAULT_THEME.globalStyles,
          },
        },
      };
    });
  };

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  if (!landingData) {
    return <div className="text-zinc-700">Cargando...</div>;
  }

  return (
    <div className="bg-zinc-950 text-white py-16 px-4 overflow-x-hidden">
      <div className="absolute top-4 left-4 z-50 pointer-events-auto mb-8">
        <ButtonVolver
          onClick={() =>
            navigate(
              `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
            )
          }
        />
      </div>
      {/* CARD */}
      <div className="max-w-7xl mx-auto space-y-10">
        {/* HEADER */}
        <div
          className="
          w-full
          rounded-lg
          border-t-2
          border-b-2
          border-cyan-300
          bg-zinc-950
          py-2
          px-8
          shadow-md
          shadow-zinc-800
          backdrop-blur-xl
        "
        >
          <div
            className="
             flex flex-col
             gap-8
             md:flex-row
             md:items-center
             md:justify-between
           "
          >
            <div className="space-y-3 flex-1">
              <h1 className="text-3xl font-bold tracking-tight">
                Editor de Apariencia
              </h1>

              <p className="text-zinc-400 text-sm max-w-md">
                Personaliza el aspecto general de la landing.
              </p>
            </div>

            <div
              className="
    flex
    flex-col
    sm:flex-row
    sm:justify-end
    sm:items-center
    gap-3
    w-full
    md:w-auto
  "
            >
              <ButtonPopup
                onClick={resetTheme}
                className="
      px-4 py-2
      rounded-lg
      bg-zinc-900
      border border-white/10
      hover:border-green-500
      transition
      flex items-center justify-center
      whitespace-nowrap
      max-w-full
    "
              >
                Restablecer
              </ButtonPopup>

              <ButtonPopup
                onClick={handleSave}
                disabled={loading || uploadingBackground}
                className="
      px-4 py-2
      rounded-lg
      bg-zinc-900
      border border-white/10
      hover:border-cyan-500
      transition
      flex items-center justify-center
      whitespace-nowrap
      max-w-full
    "
              >
                {uploadingBackground
                  ? "Subiendo imagen..."
                  : loading
                    ? "Guardando..."
                    : "Guardar cambios"}
              </ButtonPopup>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------- */}

        {/* CONTENIDO */}
        <div className="grid lg:grid-cols-[430px_1fr] gap-8">
          <section className={card}>
            <h2 className="text-xl font-semibold mb-2">Tipografía</h2>

            <p className="text-zinc-400 text-sm mb-6">
              Configura la fuente y el tamaño del texto.
            </p>

            {/* FUENTE */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-zinc-300">
                Fuente
              </label>

              <Select
                value={globalStyles?.fontFamily || "Inter"}
                onChange={(e) =>
                  updateGlobalStyles({
                    fontFamily: e.target.value,
                  })
                }
                options={[
                  { value: "Inter", label: "Inter" },
                  { value: "Poppins", label: "Poppins" },
                  { value: "Montserrat", label: "Montserrat" },
                  { value: "Roboto", label: "Roboto" },

                  { value: "Open Sans", label: "Open Sans" },
                  { value: "Lato", label: "Lato" },
                  { value: "Raleway", label: "Raleway" },
                  { value: "Nunito", label: "Nunito" },
                  { value: "DM Sans", label: "DM Sans" },
                  { value: "Work Sans", label: "Work Sans" },

                  {
                    value: "Playfair Display",
                    label: "Playfair Display (elegante)",
                  },
                  {
                    value: "Merriweather",
                    label: "Merriweather (lectura larga)",
                  },
                  { value: "Crimson Text", label: "Crimson Text (clásica)" },

                  { value: "Oswald", label: "Oswald (títulos fuertes)" },
                  { value: "Bebas Neue", label: "Bebas Neue (display)" },
                  { value: "Anton", label: "Anton (impacto)" },

                  {
                    value: "Space Grotesk",
                    label: "Space Grotesk (moderna tech)",
                  },
                  { value: "Manrope", label: "Manrope (limpia UI)" },
                  { value: "Outfit", label: "Outfit (startup style)" },
                  { value: "Plus Jakarta Sans", label: "Plus Jakarta Sans" },
                ]}
              />
            </div>
          </section>

          <section className={card}>
            <h2 className="text-xl font-semibold mb-2">Colores del texto</h2>

            <p className="text-zinc-400 text-sm mb-6">
              Personaliza los colores principales.
            </p>

            {/* Color texto */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-zinc-300">
                Color del texto
              </label>

              <Input
                type="color"
                value={globalStyles?.textColor || "#ffffff"}
                onChange={(e) =>
                  setLandingData((prev) => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      theme: {
                        ...prev.theme,
                        globalStyles: {
                          ...prev.theme?.globalStyles,
                          textColor: e.target.value,
                        },
                      },
                    };
                  })
                }
              />
            </div>

            {/* Color H1  color H2  Color H3 */}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold text-zinc-300">
                  Color H1
                </label>

                <Input
                  type="color"
                  value={globalStyles?.h1Color || "#ffffff"}
                  onChange={(e) =>
                    updateGlobalStyles({
                      h1Color: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-zinc-300">
                  Color H2
                </label>

                <Input
                  type="color"
                  value={globalStyles?.h2Color || "#ffffff"}
                  onChange={(e) =>
                    updateGlobalStyles({
                      h2Color: e.target.value,
                    })
                  }
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-zinc-300">
                  Color H3
                </label>

                <Input
                  type="color"
                  value={globalStyles?.h3Color || "#ffffff"}
                  onChange={(e) =>
                    updateGlobalStyles({
                      h3Color: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Accent color principal */}

            <div className="space-y-2">
              <label className="text-sm font-semibold text-zinc-300">
                Color principal
              </label>

              <Input
                type="color"
                value={globalStyles?.accentColor || "#00f4fb"}
                onChange={(e) =>
                  updateGlobalStyles({
                    accentColor: e.target.value,
                  })
                }
              />
            </div>
          </section>

          <section className={card}>
            <h2 className="text-xl font-semibold mb-2">Botones</h2>

            <p className="text-zinc-400 text-sm mb-6">
              Configura el aspecto de los botones.
            </p>

            {/* Color botón */}
            <div>
              <label>Color de botones</label>

              <Input
                type="color"
                value={globalStyles?.buttonColor || "#00f4fb"}
                onChange={(e) =>
                  updateGlobalStyles({
                    buttonColor: e.target.value,
                  })
                }
              />
            </div>
            {/* Color texto botón */}
            <div>
              <label>Color texto botón</label>

              <Input
                type="color"
                value={globalStyles?.buttonTextColor || "#000000"}
                onChange={(e) =>
                  updateGlobalStyles({
                    buttonTextColor: e.target.value,
                  })
                }
              />
            </div>
          </section>

          <section className={`${card} space-y-2`}>
            <h2 className="text-xl font-semibold mb-2">Fondo</h2>

            <p className="text-zinc-400 text-sm mb-6">
              Imagen o color de fondo.
            </p>

            {/* Tipo */}
            <Select
              value={theme?.backgroundType ?? "image"}
              onChange={(e) =>
                setLandingData((prev) => {
                  if (!prev) return prev;

                  return {
                    ...prev,
                    theme: {
                      ...prev.theme,
                      backgroundType: e.target.value as any,
                    },
                  };
                })
              }
              options={[
                { value: "image", label: "Imagen" },
                { value: "color", label: "Color" },
              ]}
            />
            {/* Color */}
            <div className="space-y-2">
              {theme.backgroundType === "color" && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg border border-zinc-700 overflow-hidden relative">
                    <input
                      type="color"
                      value={theme?.backgroundColor ?? "#ffffff"}
                      onChange={(e) =>
                        setLandingData((prev) => {
                          if (!prev) return prev;

                          return {
                            ...prev,
                            theme: {
                              ...prev.theme,
                              backgroundColor: e.target.value,
                            },
                          };
                        })
                      }
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    <div
                      className="w-full h-full"
                      style={{
                        backgroundColor: theme.backgroundColor || "#ffffff",
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Imagen */}

              {theme.backgroundType === "image" && (
                <Input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];

                    if (!file || perfilId == null) return;

                    // Vista previa
                    const preview = URL.createObjectURL(file);

                    setLandingData((prev) => {
                      if (!prev) return prev;

                      return {
                        ...prev,
                        theme: {
                          ...prev.theme,
                          backgroundImage: preview,
                        },
                      };
                    });

                    setUploadingBackground(true);

                    try {
                      const { url } = await uploadBackgroundLanding(
                        file,
                        perfilId,
                      );

                      setLandingData((prev) => {
                        if (!prev) return prev;

                        const next = {
                          ...prev,
                          theme: {
                            ...prev.theme,
                            backgroundImage: url,
                          },
                        };

                        return next;
                      });

                      URL.revokeObjectURL(preview);
                    } catch (err) {
                      console.error(err);
                      URL.revokeObjectURL(preview);
                    } finally {
                      setUploadingBackground(false);
                    }
                  }}
                />
              )}
            </div>

            {/* SAVE */}
            <div className="mt-8 flex justify-end gap-3">
              <ButtonSiguiente onClick={resetTheme}>
                Restablecer
              </ButtonSiguiente>

              <ButtonSiguiente
                onClick={handleSave}
                disabled={loading || uploadingBackground}
              >
                {uploadingBackground
                  ? "Subiendo imagen..."
                  : loading
                    ? "Guardando..."
                    : "Guardar"}
              </ButtonSiguiente>
            </div>
          </section>
        </div>

        {/* RIGHT */}
        <section className={`${card} sticky top-6 h-fit`}>
          <h2 className="text-xl font-semibold mb-2">Vista previa</h2>

          <p className="text-zinc-400 text-sm mb-6">
            Así visualizarán los usuarios tu landing.
          </p>

          <div
            className="w-full rounded-2xl overflow-hidden border border-white/10"
            style={{
              minHeight: 520,
              background:
                theme.backgroundType === "color"
                  ? theme.backgroundColor
                  : `url(${theme.backgroundImage}) center/cover`,
              fontFamily: globalStyles.fontFamily,
              padding: 30,
            }}
          >
            <h1
              style={{
                color: globalStyles.h1Color,
                fontWeight: 700,
              }}
            >
              Cuadranex.
            </h1>

            <h2
              style={{
                color: globalStyles.h2Color,
                marginTop: 20,
                fontWeight: 600,
              }}
            >
              Cuadranex.
            </h2>

            <h3
              style={{
                color: globalStyles.h3Color,
                marginTop: 20,
                fontWeight: 600,
              }}
            >
              Cuadranex.
            </h3>

            <p
              style={{
                color: globalStyles.textColor,
                marginTop: 20,
                lineHeight: 1.6,
              }}
            >
              Cuadranex, construimos soluciones digitales. Este texto te
              permitirá comprobar el tamaño de los párrafos, el color del texto
              y la tipografía seleccionada.
            </p>

            <button
              style={{
                marginTop: 30,
                background: globalStyles.buttonColor,
                color: globalStyles.buttonTextColor,
                padding: "12px 24px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Botón de ejemplo
            </button>
          </div>
        </section>
      </div>

      {/* ALERT */}
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}
    </div>
  );
};

export default EditarFondoWeb;
